import { useState, useEffect, useRef } from 'react';

export const useEncounterFormState = (encounter, encounterType) => {
  const [formData, setFormData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const initializing = useRef(true);

  // Helper to build form data from encounter and type
  const buildFormData = (encounter, encounterType) => {
    if (!encounter) return null;
    const baseFormData = {
      ...encounter,
      vitals: encounter.vitals || {
        bloodPressure: '',
        heartRate: '',
        temperature: '',
        respiratoryRate: '',
        oxygenSaturation: '',
        weight: '',
        height: '',
        bmi: ''
      },
      notes: {
        consultationNotes: encounter.notes?.consultationNotes || '',
        progressNotes: encounter.notes?.progressNotes || '',
        nurseNotes: encounter.notes?.nurseNotes || '',
        procedureNotes: encounter.notes?.procedureNotes || '',
        soapNotes: {
          subjective: encounter.notes?.soapNotes?.subjective || '',
          objective: encounter.notes?.soapNotes?.objective || '',
          assessment: encounter.notes?.soapNotes?.assessment || '',
          plan: encounter.notes?.soapNotes?.plan || ''
        },
        simpleNotes: encounter.notes?.simpleNotes || '',
        phoneNotes: encounter.notes?.phoneNotes || ''
      },
      symptoms: encounter.symptoms || [],
      actionPlan: encounter.actionPlan || '',
      provider: encounter.provider || {},
      department: encounter.department || '',
      unit: encounter.unit || '',
      chiefComplaint: encounter.chiefComplaint || '',
      initialAssessment: encounter.initialAssessment || '',
      room: encounter.room || '',
      roomId: encounter.roomId || '',
      bed: encounter.bed || '',
      bedId: encounter.bedId || ''
    };
    let typeSpecificData = {};
    switch (encounterType) {
      case 'outpatient':
        typeSpecificData = {
          outpatientDetails: {
            visitType: encounter.outpatientDetails?.visitType || '',
            appointmentPurpose: encounter.outpatientDetails?.appointmentPurpose || '',
            followUpRequired: encounter.outpatientDetails?.followUpRequired || false,
            followUpDate: encounter.outpatientDetails?.followUpDate || '',
            referralRequired: encounter.outpatientDetails?.referralRequired || false,
            referralTo: encounter.outpatientDetails?.referralTo || '',
            roomNumber: encounter.room || encounter.outpatientDetails?.roomNumber || '',
            bedNumber: encounter.bed || encounter.outpatientDetails?.bedNumber || ''
          }
        };
        break;
      case 'emergency':
        const validTriageLevels = ['1', '2', '3', '4', '5'];
        const sanitizedTriageLevel = encounter.emergencyDetails?.triageLevel &&
          validTriageLevels.includes(encounter.emergencyDetails.triageLevel.toString()) ?
          encounter.emergencyDetails.triageLevel : '';
        typeSpecificData = {
          emergencyDetails: {
            ...(encounter.emergencyDetails || {}),
            triageLevel: sanitizedTriageLevel,
            chiefComplaint: encounter.emergencyDetails?.chiefComplaint || '',
            mechanismOfInjury: encounter.emergencyDetails?.mechanismOfInjury || '',
            timeOfInjury: encounter.emergencyDetails?.timeOfInjury || '',
            vitalSigns: encounter.emergencyDetails?.vitalSigns || '',
            painScale: encounter.emergencyDetails?.painScale || '',
            consciousness: encounter.emergencyDetails?.consciousness || '',
            disposition: encounter.emergencyDetails?.disposition || '',
            followUpRequired: encounter.emergencyDetails?.followUpRequired || false,
            followUpDate: encounter.emergencyDetails?.followUpDate || '',
            roomNumber: encounter.room || encounter.emergencyDetails?.roomNumber || '',
            bedNumber: encounter.bed || encounter.emergencyDetails?.bedNumber || ''
          }
        };
        break;
      case 'observation':
        typeSpecificData = {
          observationDetails: {
            observationType: encounter.observationDetails?.observationType || '',
            expectedDuration: encounter.observationDetails?.expectedDuration || '',
            roomNumber: encounter.room || encounter.observationDetails?.roomNumber || '',
            bedNumber: encounter.bed || encounter.observationDetails?.bedNumber || '',
            admittingPhysician: encounter.observationDetails?.admittingPhysician || '',
            attendingPhysician: encounter.observationDetails?.attendingPhysician || '',
            primaryDiagnosis: encounter.observationDetails?.primaryDiagnosis || '',
            secondaryDiagnosis: encounter.observationDetails?.secondaryDiagnosis || '',
            dischargeDisposition: encounter.observationDetails?.dischargeDisposition || '',
            lengthOfStay: encounter.observationDetails?.lengthOfStay || ''
          }
        };
        break;
      case 'telehealth':
        typeSpecificData = {
          telehealthDetails: {
            platform: encounter.telehealthDetails?.platform || '',
            connectionQuality: encounter.telehealthDetails?.connectionQuality || '',
            patientLocation: encounter.telehealthDetails?.patientLocation || '',
            technicalIssues: encounter.telehealthDetails?.technicalIssues || false,
            technicalIssuesDescription: encounter.telehealthDetails?.technicalIssuesDescription || '',
            followUpRequired: encounter.telehealthDetails?.followUpRequired || false,
            followUpDate: encounter.telehealthDetails?.followUpDate || '',
            inPersonFollowUpRequired: encounter.telehealthDetails?.inPersonFollowUpRequired || false
          }
        };
        break;
      case 'surgical':
        typeSpecificData = {
          surgicalDetails: {
            operatingRoom: encounter.surgicalDetails?.operatingRoom || '',
            procedureType: encounter.surgicalDetails?.procedureType || '',
            surgeon: encounter.surgicalDetails?.surgeon || '',
            anesthesiologist: encounter.surgicalDetails?.anesthesiologist || '',
            procedureDate: encounter.surgicalDetails?.procedureDate || '',
            estimatedDuration: encounter.surgicalDetails?.estimatedDuration || '',
            preOpAssessment: encounter.surgicalDetails?.preOpAssessment || '',
            postOpInstructions: encounter.surgicalDetails?.postOpInstructions || '',
            followUpRequired: encounter.surgicalDetails?.followUpRequired || false,
            followUpDate: encounter.surgicalDetails?.followUpDate || ''
          }
        };
        break;
      case 'inpatient':
        typeSpecificData = {
          inpatientDetails: {
            admissionType: encounter.inpatientDetails?.admissionType || '',
            expectedLengthOfStay: encounter.inpatientDetails?.expectedLengthOfStay || '',
            admittingPhysician: encounter.inpatientDetails?.admittingPhysician || '',
            attendingPhysician: encounter.inpatientDetails?.attendingPhysician || '',
            primaryDiagnosis: encounter.inpatientDetails?.primaryDiagnosis || '',
            secondaryDiagnosis: encounter.inpatientDetails?.secondaryDiagnosis || '',
            dischargeDisposition: encounter.inpatientDetails?.dischargeDisposition || '',
            roomNumber: encounter.room || encounter.inpatientDetails?.roomNumber || '',
            bedNumber: encounter.bed || encounter.inpatientDetails?.bedNumber || ''
          }
        };
        break;
      default:
        break;
    }
    return {
      ...baseFormData,
      ...typeSpecificData
    };
  };

  // Initialize formData and originalData only when encounter or type changes
  useEffect(() => {
    if (encounter) {
      const newFormData = buildFormData(encounter, encounterType);
      setFormData(newFormData);
      setOriginalData(JSON.stringify(newFormData));
      setHasChanges(false);
      initializing.current = false;
    }
     
  }, [encounter, encounterType, encounter?.department, encounter?.room, encounter?.bed, encounter?.bedId, encounter?.roomId, encounter?.updatedAt]);

  // Track changes
  useEffect(() => {
    if (!initializing.current && formData && originalData) {
      const currentData = JSON.stringify(formData);
      setHasChanges(currentData !== originalData);
    }
  }, [formData, originalData]);

  const resetChanges = () => {
    if (originalData) {
      setFormData(JSON.parse(originalData));
      setHasChanges(false);
    }
  };

  return {
    formData,
    setFormData,
    hasChanges,
    resetChanges
  };
}; 